import React, { useState, useEffect } from 'react';
import { Layout } from './components/layout';
import ScrollToTop from './components/ScrollToTop';
import Hero from './components/Hero';
import { CloudAgnostic, DataMasking, PromptImprovement, PrivateRAG, MultiLLMSupport } from './components/features';
import { homeSEO } from './constants/seo';

/**
 * Main App component for the Raidu website
 * Serves as the entry point for the application UI
 * @returns {JSX.Element} The rendered App component
 */
const App: React.FC = () => {
  // State for tracking page load status
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  
  // Effect to handle component mount
  useEffect(() => {
    try {
      // Log component mount
      console.log('App component mounted');
      
      // Set loaded state to true
      setIsLoaded(true);
      
      // Cleanup function
      return () => {
        console.log('App component unmounted');
      };
    } catch (error) {
      // Log any errors in the effect
      console.error('Error in App component effect:', error);
    }
  }, []);

  try {
    return (
      <Layout seo={homeSEO}>
        <ScrollToTop />
        {/* Hero section */}
        <Hero />
        
        {/* Cloud Agnostic section */}
        <div id="cloud-agnostic">
          <CloudAgnostic />
        </div>
        
        {/* Data Masking section */}
        <div id="data-masking">
          <DataMasking />
        </div>
        
        {/* Prompt Improvement section */}
        <div id="prompt-improvement">
          <PromptImprovement />
        </div>
        
        {/* Private RAG section */}
        <div id="private-rag">
          <PrivateRAG />
        </div>
        
        {/* Multi-LLM Support section */}
        <div id="multi-llm-support">
          <MultiLLMSupport />
        </div>
        
        {/* Status indicator */}
        {!isLoaded && (
          <div className="fixed bottom-4 right-4 p-3 bg-yellow-100 rounded text-yellow-800 shadow-lg">
            Loading application...
          </div>
        )}
      </Layout>
    );
  } catch (error) {
    console.error('Error rendering App component:', error);
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="p-6 max-w-sm bg-white rounded-lg shadow-xl">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">An error occurred while rendering the application.</p>
          <p className="text-sm text-gray-500 mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }
};

export default App;
